import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import React, { Fragment, useState } from 'react';

import { useNumberFormatter } from '@mint-lib/formatting-hooks';
import { Chat } from '@mint-sdk/api-ai-assistant';

import {
  Table,
  TableBody,
  TableBodyCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@myn/mui';

import { dataTableValueFormatter, getColumns } from './helpers.js';

type Props = {
  dataframeData: Chat.AiChatMessageRecordType['message']['additionalMetadata']['dataframe'];
  type?: 'mobile' | 'desktop' | 'agentic';
};

const tableConfig = {
  mobile: {
    isScrollable: true,
    tableStyles: {
      width: '100%',
      display: 'table',
      tableLayout: 'fixed',
    },
  },
  desktop: {
    isScrollable: false,
    tableStyles: {},
  },
  agentic: {
    isScrollable: false,
    tableStyles: {},
  },
};

const AiDataTable = ({ dataframeData = [], type = 'agentic' }: Props) => {
  const numberFormatter = useNumberFormatter();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const columnQty = Object.keys(dataframeData[0]).length;
  const table = useReactTable({
    data: dataframeData,
    // @ts-ignore - TODO: fix this. It's changes
    columns: getColumns(dataframeData[0]),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  const totalPages = table.getPageCount();

  return (
    <>
      <div
        className={tableConfig[type].isScrollable ? 'overflow-auto' : undefined}
      >
        <TableContainer sx={tableConfig[type].tableStyles}>
          <Table
            sx={{
              width: columnQty <= 5 ? '100%' : table.getTotalSize(),
              minWidth: '100%',
            }}
          >
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Fragment key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </Fragment>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      const value = cell.getValue();
                      return (
                        <TableBodyCell key={cell.id}>
                          {dataTableValueFormatter(value, numberFormatter)}
                        </TableBodyCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      {totalPages > 1 && (
        <div>
          <TablePagination
            onPageChange={(_, page) => table.setPageIndex(page)}
            count={table.getRowCount()}
            rowsPerPage={pagination.pageSize}
            page={pagination.pageIndex}
          />
        </div>
      )}
    </>
  );
};

export { AiDataTable };
